import { makeAutoObservable, runInAction } from 'mobx';
import { Camera, DataCaptureContext, DataCaptureView, FrameSourceState, ImageFrameSource, Logger } from '@scandit/web-datacapture-core';
import {
  DriverLicense,
  IdCapture,
  IdCaptureOverlay,
  IdCaptureScanner,
  IdCaptureSettings,
  IdCard,
  IdImageType,
  Passport,
  Region,
  SingleSideScanner,
  idCaptureLoader,
} from '@scandit/web-datacapture-id';

export enum IdDocumentRecognitionSDKStatus {
  Uninitialized = 'uninitialized',
  Initializing = 'initializing',
  Ready = 'ready',
  Error = 'error',
}

type VideoRef = {
  current: HTMLVideoElement | null;
};

class IdDocumentRecognitionStore {
  dataCaptureContext: DataCaptureContext | null = null;
  camera: Camera | null = null;
  idCapture: IdCapture | null = null;
  dataCaptureView: DataCaptureView | null = null;
  status = IdDocumentRecognitionSDKStatus.Uninitialized;

  private idCaptureOverlay: IdCaptureOverlay | null = null;
  private _isScanning = false;
  private _isElementAttached = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  async init(licenseKey: string | null, libraryLocation: string | null): Promise<void> {
    if (this.status !== IdDocumentRecognitionSDKStatus.Uninitialized) {
      return;
    }

    if (!licenseKey || !libraryLocation) {
      return;
    }

    this.setStatus(IdDocumentRecognitionSDKStatus.Initializing);

    let context: DataCaptureContext | null = null;

    try {
      context = await DataCaptureContext.forLicenseKey(licenseKey, {
        libraryLocation,
        moduleLoaders: [
          idCaptureLoader({
            enableVIZDocuments: true,
          }),
        ],
        logLevel: Logger.Level.Warn,
      });

      const camera = Camera.pickBestGuess();

      await camera.applySettings(IdCapture.recommendedCameraSettings);
      await context.setFrameSource(camera);

      const settings = new IdCaptureSettings();

      settings.acceptedDocuments.push(new DriverLicense(Region.Any));
      settings.acceptedDocuments.push(new IdCard(Region.Any));
      settings.acceptedDocuments.push(new Passport(Region.Any));

      settings.setShouldPassImageTypeToResult(IdImageType.CroppedDocument, true);
      settings.setShouldPassImageTypeToResult(IdImageType.Face, true);
      settings.setShouldPassImageTypeToResult(IdImageType.Frame, true);

      settings.scanner = new IdCaptureScanner({
        physicalDocument: new SingleSideScanner(true, true, true),
      });

      const idCapture = await IdCapture.forContext(context, settings);

      const view = new DataCaptureView();
      await view.setContext(context);

      const overlay = await IdCaptureOverlay.withIdCaptureForView(idCapture, view);

      runInAction(() => {
        this.dataCaptureContext = context;
        this.camera = camera;
        this.idCapture = idCapture;
        this.dataCaptureView = view;
        this.idCaptureOverlay = overlay;
        this.status = IdDocumentRecognitionSDKStatus.Ready;
      });
    } catch (error) {
      console.error('Failed to initialize Scandit ID Capture', error);

      if (context) {
        await context.dispose().catch((disposeError) => {
          console.error('Failed to dispose Scandit context', disposeError);
        });
      }

      runInAction(() => {
        this.clearReferences();
        this.status = IdDocumentRecognitionSDKStatus.Error;
      });
    }
  }

  async enableScanning(enabled: boolean): Promise<void> {
    if (this.status !== IdDocumentRecognitionSDKStatus.Ready || !this.dataCaptureContext || !this.camera || !this.idCapture) {
      return;
    }

    const alreadyInDesiredState = enabled ? this._isScanning && this.dataCaptureContext.frameSource === this.camera : !this._isScanning;

    if (alreadyInDesiredState) {
      return;
    }

    this._isScanning = enabled;

    this.dataCaptureView?.setProgressBarMessage('');
    this.dataCaptureView?.showProgressBar();

    try {
      if (enabled) {
        if (this.dataCaptureContext.frameSource !== this.camera) {
          await this.dataCaptureContext.setFrameSource(this.camera);
        }

        await this.idCapture.setEnabled(true);
        await this.camera.switchToDesiredState(FrameSourceState.On);
      } else {
        await this.idCapture.setEnabled(false);

        await this.dataCaptureContext.frameSource?.switchToDesiredState(FrameSourceState.Off);
      }
    } catch (error) {
      runInAction(() => {
        this._isScanning = !enabled;
      });

      throw error;
    } finally {
      this.dataCaptureView?.hideProgressBar();
    }
  }

  connectToElement(htmlElement: HTMLElement): void {
    if (!this.dataCaptureView || !this.idCaptureOverlay) {
      return;
    }

    this.detachElement();

    this.dataCaptureView.connectToElement(htmlElement);
    this._isElementAttached = true;
  }

  detachElement(): void {
    if (!this._isElementAttached) {
      return;
    }

    this.dataCaptureView?.detachFromElement();
    this._isElementAttached = false;
  }

  async cleanup(): Promise<void> {
    const context = this.dataCaptureContext;
    const currentFrameSource = context?.frameSource;

    this.detachElement();

    try {
      await this.idCapture?.setEnabled(false);
      await currentFrameSource?.switchToDesiredState(FrameSourceState.Off);

      if (this.camera && this.camera !== currentFrameSource) {
        await this.camera.switchToDesiredState(FrameSourceState.Off);
      }

      await context?.setFrameSource(null);
      await context?.removeAllModes();
    } finally {
      try {
        await context?.dispose();
      } finally {
        runInAction(() => {
          this.clearReferences();
          this.status = IdDocumentRecognitionSDKStatus.Uninitialized;
        });
      }
    }
  }

  async scanStream(videoRef: VideoRef): Promise<void> {
    const video = videoRef.current;

    if (
      this.status !== IdDocumentRecognitionSDKStatus.Ready ||
      !video ||
      !this.dataCaptureContext ||
      video.videoWidth === 0 ||
      video.videoHeight === 0
    ) {
      return;
    }

    const canvas = document.createElement('canvas');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const canvasContext = canvas.getContext('2d');

    if (!canvasContext) {
      return;
    }

    canvasContext.drawImage(video, 0, 0, canvas.width, canvas.height);

    try {
      const imageData = canvasContext.getImageData(0, 0, canvas.width, canvas.height);

      const source = await ImageFrameSource.fromImageData(imageData);

      await this.dataCaptureContext.setFrameSource(source);
      await source.switchToDesiredState(FrameSourceState.On);
    } catch (error) {
      console.error('Failed to scan video frame', error);
    }
  }

  setStatus(status: IdDocumentRecognitionSDKStatus): void {
    this.status = status;
  }

  private clearReferences(): void {
    this.dataCaptureContext = null;
    this.camera = null;
    this.idCapture = null;
    this.dataCaptureView = null;
    this.idCaptureOverlay = null;
    this._isScanning = false;
    this._isElementAttached = false;
  }
}

const idDocumentRecognitionContext = new IdDocumentRecognitionStore();
export default idDocumentRecognitionContext;
